import React, { useState, useEffect } from "react";
import UserSelection from "./UserSentiment/UserSelection";
import UserDetails from "./UserSentiment/UserDetails";
import ProjectSelection from "./ProjectSentiment/ProjectSelection";
import ChannelDetails from "./ProjectSentiment/Channel/ChannelDetails";
import UserAnalysis from "./UserSentiment/UserAnalysis";
import { getDocs, collection } from "firebase/firestore";
import { db } from "../../firebase/firebase";
import { useColorMode } from "@chakra-ui/react";
import { getThemeClasses } from "../../utils/themeUtils";
import CustomDropdown from "../../components/CustomDropdown";

export type Project = {
  id: string;
  name: string;
  description?: string;
  channels: string[];
};

export type User = {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  role: string;
};

interface SentimentAnalysisProps {
  color: string;
  bg: string;
  organizationId: string;
  projects: Project[];
  users: User[];
}

const SentimentAnalysis: React.FC<SentimentAnalysisProps> = ({
  bg,
  color,
  projects,
  users,
  organizationId,
}) => {
  const [viewType, setViewType] = useState<"user" | "channel">("channel");
  const [selectedProject, setSelectedProject] = useState<string | null>(null);
  const [selectedUser, setSelectedUser] = useState<string | null>(null);
  const [channels, setChannels] = useState<{id: string, name: string}[]>([]);
  const [selectedChannel, setSelectedChannel] = useState<string | null>(null);

  // Clear selections when switching tabs
  useEffect(() => {
    setSelectedProject(null);
    setSelectedUser(null);
    setSelectedChannel(null);
  }, [viewType]);

  // Fetch channels when project is selected
  useEffect(() => {
    if (!selectedProject) {
      setChannels([]);
      setSelectedChannel(null);
      return;
    }

    const fetchChannels = async () => {
      const chanRef = collection(
        db,
        `organization/${organizationId}/projects/${selectedProject}/channels`
      );
      const snap = await getDocs(chanRef);
      const arr = snap.docs.map((d) => ({
        id: d.id,
        name: d.data().name || "Unnamed",
      }));
      setChannels(arr);
      setSelectedChannel(null);
    };

    fetchChannels();
  }, [organizationId, selectedProject]);

  const { colorMode } = useColorMode();
  const isDark = colorMode === "dark";
  const theme = getThemeClasses(isDark);

  return (
    <div className={`w-full  py-0 ${theme.background}`}>
      {/* Top Gradient Section */}
      <div className={`flex justify-between items-center p-4 ${theme.headerGradient} rounded-md mb-4 border ${theme.border} relative`}>
        {isDark && (
          <>
            <span className={theme.headerOverlayGlow} />
            <span className={theme.headerOverlayTopLine} />
            <span className={theme.headerOverlayBottomGlow} />
            <span className={theme.headerOverlaySweep} />
          </>
        )}
        <div className="flex flex-col space-y-1">
          <span className={`text-2xl font-bold ${theme.text}`}>
            Sentiment Analysis
          </span>
          <span className={`text-xs ${theme.textMuted}`}>
            Gain insights into user and channel sentiment across your organization
          </span>
        </div>
      </div>
      {/* Top Section: Dropdowns */}
      <div className="flex flex-row space-x-6 w-full mb-4 items-center px-5">
        {/* Tab Selection Dropdown */}
          <div className="flex items-center space-x-2">
            <label className={`text-sm font-semibold whitespace-nowrap ${theme.text}`}>View type:</label>
            <CustomDropdown
              value={viewType}
              onChange={(value) => setViewType(value as "user" | "channel")}
              isDark={isDark}
              className="w-[220px] h-9 text-sm font-medium focus:outline-none focus:ring-0 transition pl-2 pr-8"
              options={[
                { value: "channel", label: "Channels" },
                { value: "user", label: "Users" }
              ]}
            />
          </div>

        {viewType === "channel" && (
          <>
            {/* Project Selection */}
            <div className="flex items-center space-x-2">
              <label className={`text-sm font-semibold whitespace-nowrap ${theme.text}`}>Project:</label>
              {projects.length > 0 ? (
                <CustomDropdown
                  value={selectedProject || ""}
                  onChange={(value) => setSelectedProject(value || null)}
                  isDark={isDark}
                  className="w-[220px] h-9 text-sm font-medium focus:outline-none focus:ring-0 transition pl-2 pr-8"
                  options={[
                    { value: "", label: "Choose a project" },
                    ...projects.map((project) => ({ value: project.id, label: project.name }))
                  ]}
                />
              ) : (
                <div className={`w-[220px] h-9 flex items-center justify-center text-sm rounded-lg shadow-sm px-3 ${theme.textMuted} ${isDark ? 'bg-[#14161B]' : 'bg-white'} border ${theme.border}`}>
                  No projects available
                </div>
              )}
            </div>

            {/* Channel Selection */}
            {selectedProject && (
              <div className="flex items-center space-x-2">
                <label className={`text-sm font-semibold whitespace-nowrap ${theme.text}`}>Channel:</label>
                {channels.length > 0 ? (
                  <CustomDropdown
                    value={selectedChannel || ""}
                    onChange={(value) => setSelectedChannel(value || null)}
                    isDark={isDark}
                    className="w-[220px] h-9 text-sm font-medium focus:outline-none focus:ring-0 transition pl-2 pr-8"
                    options={[
                      { value: "", label: "Choose a channel" },
                      ...channels.map((channel) => ({ value: channel.id, label: channel.name }))
                    ]}
                  />
                ) : (
                   <div className={`w-[220px] h-9 flex items-center justify-center text-sm rounded-lg shadow-sm px-3 ${theme.textMuted} ${isDark ? 'bg-[#14161B]' : 'bg-white'} border ${theme.border}`}>
                     No channels available
                   </div>
                )
                }
              </div>
            )}
          </>
        )}

        {viewType === "user" && (
          <div className="flex items-center space-x-2">
            <label className={`text-sm font-semibold whitespace-nowrap ${theme.text}`}>User:</label>
            <CustomDropdown
              value={selectedUser || ""}
              onChange={(value) => setSelectedUser(value || null)}
              isDark={isDark}
              className="w-[220px] h-9 text-sm font-medium focus:outline-none focus:ring-0 transition pl-2 pr-8"
              options={[
                { value: "", label: "Choose a user" },
                ...users.map((user) => ({ value: user.id, label: `${user.firstName} ${user.lastName}` }))
              ]}
            />
          </div>
        )}
      </div>

      {/* Full-Width Bottom Section: Details Display */}
      <div className="w-full flex justify-center">
        <div className="w-full max-w-[1500px]">
          {viewType === "user" ? (
            selectedUser ? (
              <UserAnalysis
                users={users}
                selectedUser={selectedUser}
              />
            ) : (
              <div className={`h-[200px] mt-5 rounded-lg border border-dashed flex items-center justify-center ${isDark ? 'bg-[#1A1C1F] border-[#2E2F33]' : 'bg-white border-gray-200'}`}>
                <span className={`${theme.textMuted}`}>
                  Please select a user to view their sentiment analysis
                </span>
              </div>
            )
          ) : ( // viewType === "channel"
            selectedProject && selectedChannel ? (
              <ChannelDetails
                organizationId={organizationId}
                projects={projects}
                selectedProject={selectedProject}
                selectedChannelId={selectedChannel}
                users={users}
              />
            ) : selectedProject ? (
              <div className={`h-[200px] mt-5 rounded-lg border border-dashed flex items-center justify-center ${isDark ? 'bg-[#1A1C1F] border-[#2E2F33]' : 'bg-white border-gray-200'}`}>
                <span className={`${theme.textMuted}`}>
                  Please select a channel to view sentiment analysis
                </span>
              </div>
            ) : projects.length > 0 ? (
              <div className={`h-[200px] mt-5 rounded-lg border border-dashed flex items-center justify-center ${isDark ? 'bg-[#1A1C1F] border-[#2E2F33]' : 'bg-white border-gray-200'}`}>
                <span className={`${theme.textMuted}`}>
                  Please select a project to view sentiment analysis
                </span>
              </div>
            ) : (
              <div className={`h-[200px] mt-5 rounded-lg border border-dashed flex items-center justify-center ${isDark ? 'bg-[#1A1C1F] border-[#2E2F33]' : 'bg-white border-gray-200'}`}>
                <span className={`${theme.textMuted}`}>
                  No projects available for sentiment analysis
                </span>
              </div>
            )
          )}
        </div>
      </div>
    </div>
  );
};

export default SentimentAnalysis;
